import { Divider as CoreDivider, Skeleton } from '@hopskipdrive/react-components';
import { useAuthQuery } from '@rideiq/auth/hooks/useAuthQuery';
import { HIDDEN_SERVICE_CODES } from '@rideiq/riders/constants/riderServices';
import { colorPalette, colorTokens } from '@rideiq/shared/components/variables';
import styled from 'styled-components';

import { RiderCard } from '../../components/RiderCard';
import { useSupportSettingsCatalog } from '../RiderForm/hooks/useSupportSettingsCatalog';
import { getCategoryLabel, orderCategories } from '../RiderForm/sections/RiderServicesSection/helpers/orderCategories';
import type { SupportSetting } from '../RiderForm/types';

const RECORDING_SERVICE_CODES = ['DR', 'AR'];

interface RiderServicesReadOnlyCardProps {
  codeWord: string;
  riderNotes: string;
  supportSettings: SupportSetting[];
  services?: RiderService[];
  boosterSeat?: boolean;
  frontSeatAuthorized?: boolean;
  isFetching?: boolean;
}

export const RiderServicesReadOnlyCard = ({
  codeWord,
  riderNotes,
  supportSettings,
  services = [],
  boosterSeat,
  frontSeatAuthorized,
  isFetching,
}: RiderServicesReadOnlyCardProps) => {
  const catalog = useSupportSettingsCatalog();
  const { userFeatures } = useAuthQuery();
  const hidePhi = !!userFeatures?.hidePhi;

  const isLoading = Boolean(isFetching) || catalog.status === 'loading';
  const orderedCategories = !hidePhi && catalog.status === 'ready' ? orderCategories(catalog.categories) : [];

  const visibleServices = services.filter((s) => !HIDDEN_SERVICE_CODES.includes(s.code));
  const riderServices = visibleServices
    .filter((s) => !RECORDING_SERVICE_CODES.includes(s.code))
    .sort((a, b) => a.name.localeCompare(b.name));
  const recordingServices = visibleServices
    .filter((s) => RECORDING_SERVICE_CODES.includes(s.code))
    .sort((a, b) => a.name.localeCompare(b.name));

  const seatingItems: string[] = [];

  if (frontSeatAuthorized) {
    seatingItems.push('Can sit up front');
  }
  if (boosterSeat) {
    seatingItems.push('Needs a booster seat');
  }

  return (
    <RiderCard
      gradientOverlay={true}
      height="227px"
      heading={<CardLabel>Rider Services</CardLabel>}
      data-testid="rider-services-readonly-card"
    >
      {isLoading ? (
        <>
          <Skeleton width={80} />
          <Skeleton width={120} />
          <Skeleton width={100} />
        </>
      ) : (
        <>
          {codeWord && (
            <Section>
              <SectionHeader>Code Word</SectionHeader>
              <SectionBody>{codeWord}</SectionBody>
            </Section>
          )}

          {seatingItems.length > 0 && (
            <>
              <Divider aria-hidden="true" />
              <Section>
                <SectionHeader>Seating</SectionHeader>
                <BulletList>
                  {seatingItems.map((item) => (
                    <li key={item}>{item}</li>
                  ))}
                </BulletList>
              </Section>
            </>
          )}

          {riderServices.length > 0 && (
            <>
              <Divider aria-hidden="true" />
              <Section>
                <SectionHeader>Rider services</SectionHeader>
                <BulletList>
                  {riderServices.map((s) => (
                    <li key={s.code}>{s.name}</li>
                  ))}
                </BulletList>
              </Section>
            </>
          )}

          {recordingServices.length > 0 && (
            <>
              <Divider aria-hidden="true" />
              <Section>
                <SectionHeader>Ride Recording</SectionHeader>
                <BulletList>
                  {recordingServices.map((s) => (
                    <li key={s.code}>{s.name}</li>
                  ))}
                </BulletList>
              </Section>
            </>
          )}

          {orderedCategories.map((category) => {
            const selectedItems = category.supportSettings.filter((catalogItem) =>
              supportSettings.some((s) => s.supportType === catalogItem.supportType && s.selected)
            );

            if (selectedItems.length === 0) {
              return null;
            }

            return (
              <div key={category.name}>
                <Divider aria-hidden="true" />
                <Section>
                  <SectionHeader>{getCategoryLabel(category.name)}</SectionHeader>
                  <BulletList>
                    {selectedItems.map((item) => (
                      <li key={item.supportType}>{item.label}</li>
                    ))}
                  </BulletList>
                </Section>
              </div>
            );
          })}

          {riderNotes && (
            <>
              <Divider aria-hidden="true" />
              <Section>
                <SectionHeader>Rider Notes</SectionHeader>
                <SectionBody>
                  <i>{riderNotes}</i>
                </SectionBody>
              </Section>
            </>
          )}
        </>
      )}
    </RiderCard>
  );
};

const CardLabel = styled.h3`
  font-size: 14px;
  font-weight: 500;
  color: ${colorTokens.text.dark};
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Divider = styled(CoreDivider)`
  margin: 8px 0;
`;

const SectionHeader = styled.p`
  font-weight: 500;
  font-size: 14px;
  color: ${colorTokens.text.dark};
  margin: 0;
`;

const SectionBody = styled.p`
  font-weight: 400;
  font-size: 13px;
  line-height: 20px;
  color: ${colorPalette.neutralVariant40};
  margin: 0;
`;

const BulletList = styled.ul`
  font-weight: 400;
  font-size: 13px;
  line-height: 0;
  color: ${colorPalette.neutralVariant40};
  margin: 0;
  padding-left: 0;
  list-style: disc;

  li {
    margin-left: 26px;
    line-height: 20px;
  }
`;
